/**
 * SettingsPersistence — save and load user settings to localStorage.
 *
 * Persists audio volume levels and fullscreen preference.
 * Gracefully handles missing or corrupt data.
 */

const STORAGE_KEY = 'game_settings';

export interface PersistedSettings {
  masterVolume: number;
  sfxVolume: number;
  musicVolume: number;
  fullscreen: boolean;
  showTouchControls?: boolean;
  dyslexicFont?: boolean;
  largeText?: boolean;
  highContrastSubtitles?: boolean;
  showDebugOverlay?: boolean;
}

const DEFAULTS: PersistedSettings = {
  masterVolume: 0.7,
  sfxVolume: 0.8,
  musicVolume: 0.5,
  fullscreen: false,
  showTouchControls: true,
  dyslexicFont: false,
  largeText: false,
  highContrastSubtitles: false,
  showDebugOverlay: false,
};

export function loadSettings(): PersistedSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return {
      masterVolume: clampVolume(parsed.masterVolume ?? DEFAULTS.masterVolume),
      sfxVolume: clampVolume(parsed.sfxVolume ?? DEFAULTS.sfxVolume),
      musicVolume: clampVolume(parsed.musicVolume ?? DEFAULTS.musicVolume),
      fullscreen: typeof parsed.fullscreen === 'boolean' ? parsed.fullscreen : DEFAULTS.fullscreen,
      showTouchControls: typeof parsed.showTouchControls === 'boolean' ? parsed.showTouchControls : true,
      dyslexicFont: typeof parsed.dyslexicFont === 'boolean' ? parsed.dyslexicFont : false,
      largeText: typeof parsed.largeText === 'boolean' ? parsed.largeText : false,
      highContrastSubtitles: typeof parsed.highContrastSubtitles === 'boolean' ? parsed.highContrastSubtitles : false,
      showDebugOverlay: typeof parsed.showDebugOverlay === 'boolean' ? parsed.showDebugOverlay : false,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveSettings(settings: PersistedSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
  }
}

function clampVolume(v: unknown): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0.5;
  return Math.max(0, Math.min(1, n));
}
